import Typography from '@mui/material/Typography'
import { DataEditContextPasswordField } from 'Components/reusable/DataContext/fields/DataEditContextPasswordField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import { getAccountVerificationInitialData } from 'Components/reusable/DataContext/InitialData'
import { accountVerificationSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { authAPI } from 'api/AuthAPI'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { useNavigate, useLocation, useParams } from 'react-router'
import { IActivationUserData } from 'types/userInterfaces'

import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DataContext, { NewOrExisting } from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useLogin, useUser } from 'hooks'
import React, { useEffect, useState } from 'react'
import { IVerifyAccount } from 'types/accountInterfaces'
import { IError } from 'types/error'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import LoginPageCard from 'Components/reusable/CaleoCustomComponents/LoginPageCard'
import { useTranslation } from 'react-i18next'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import caleoLogo from 'assets/images/caleoLogo.svg'

type T = IVerifyAccount

/**
 * Renders the Account Verification component.
 *
 * @return {React.FC} The rendered Account Verification component.
 * @notExported
 */
const VerifyAccount: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useParams()
  const { setNotificationPopup } = useNotificationPopup()
  const { t } = useTranslation()

  const [userData, setUserData] = useState<IActivationUserData>({})
  const [data, setData] = useState<NewOrExisting<T>>(getAccountVerificationInitialData())
  const schema = accountVerificationSchema()
  const localeBase = 'password'
  const [submitLocally, setSubmitLocally] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()
  const [passwordError, setPasswordError] = useState<string>()
  const { loggingIn, loggedIn, loginLoading } = useLogin()
  const { groupsReady, groups } = useUser()

  const buttonsEnabled = !loggingIn

  useEffect(() => {
    const controller = new AbortController()

    if (!token) {
      navigate({ pathname: '/login' })
      return setNotificationPopup({
        message: t('password.activateAccount.invalidToken'),
        type: 'error',
        duration: 'long',
      })
    }

    ;(async () => {
      try {
        const userData = await authAPI.getUserDataByToken(token, controller)
        if (location.state && location.state.error) {
          setPasswordError(location.state.error)
          setSubmitLocally(false)
        }
        setUserData(userData)
      } catch (error) {
        const err = error as IError
        if (err.status === 404) {
          navigate({ pathname: '/login' })
          setNotificationPopup({
            message: t('password.activateAccount.invalidToken'),
            type: 'error',
            duration: 'long',
          })
        } else {
          setBackendError(err)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [location.state, navigate, setNotificationPopup, t, token])

  useEffect(() => {
    if (passwordError) {
      setPasswordError(undefined)
    }
  }, [data.password.length, passwordError])

  useEffect(() => {
    if (loggedIn && groupsReady) {
      if (location.state?.url) {
        navigate(location.state?.url)
      } else if (groups && groups.includes('sales')) {
        navigate('/employees')
      } else {
        navigate('/profile')
      }
    }
  }, [loggedIn, groupsReady, location.state?.url, groups, navigate])

  useEffect(() => {
    if (submitLocally) {
      navigate('/activate', {
        state: {
          token: token,
          userData: userData,
          password: data.password,
          previous: location.pathname,
          search: location.search,
        },
      })
    }
  }, [data.password, location.pathname, location.search, navigate, submitLocally, token, userData])

  if (!data) {
    return <LoadingIndicator />
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} disableContinue={true} />
  }

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid size={12}>
        <img src={caleoLogo} alt="logo" style={{ width: '159px', padding: '18px' }} />
      </Grid>
      {loginLoading ? (
        <LoadingIndicator />
      ) : (
        <DataContext
          data={data}
          onChange={setData}
          onSubmit={() => setSubmitLocally(true)}
          schema={schema}
          localeBase={localeBase}
        >
          <Grid container justifyContent="center" alignItems="center" size={12}>
            <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4, xl: 3 }}>
              <LoginPageCard header={t('password.activateAccount')}>
                <Grid container>
                  <Typography>{t('password.instructions')}</Typography>
                  <Grid mt={2} size={12}>
                    <DataEditContextPasswordField<IVerifyAccount>
                      field="password"
                      required
                      fullWidth
                      showPassowordOrHide={true}
                    />
                  </Grid>
                  <Grid mt={2} mb={passwordError ? 0 : 2} size={12}>
                    <DataEditContextPasswordField<IVerifyAccount>
                      field="repeatPassword"
                      required
                      fullWidth
                      showPassowordOrHide={true}
                    />
                  </Grid>
                  {passwordError && (
                    <Grid mb={2} size={12}>
                      <CardContentText type="error">{t(passwordError)}</CardContentText>
                    </Grid>
                  )}
                </Grid>
                <Grid container justifyContent="center" spacing={1} alignItems="center">
                  <Grid container alignItems="center" spacing={1} size={12}>
                    <Grid size="auto">
                      <DataEditContextValidation>
                        {({ valid }) => (
                          <CaleoPrimaryButton
                            testId="button-verify-account-submit"
                            valid={valid || !buttonsEnabled}
                            clickAction={() => setSubmitLocally(true)}
                            label={t(`submit`)}
                            loading={false}
                          />
                        )}
                      </DataEditContextValidation>
                    </Grid>
                  </Grid>
                </Grid>
              </LoginPageCard>
            </Grid>
          </Grid>
        </DataContext>
      )}
    </Grid>
  )
}

export default VerifyAccount
